import { useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  IndexTable,
  InlineStack,
  Modal,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/types/api';
import {
  useCommsHealth,
  useCommsOrder,
  useCommsOrderAction,
  useCommsOrders,
  useCommsSettings,
  useUpdateCommsSettings,
  type CommsAction,
  type CommsEvent,
  type CommsJob,
  type CommsOrder,
  type CommsOrderDetail,
  type CommsOrdersResponse,
  type CommsOrderStatus,
} from '@/queries/comms';

type BadgeTone = 'info' | 'success' | 'warning' | 'critical' | 'attention' | 'new';

const STATUS_LABEL: Record<CommsOrderStatus, string> = {
  new: 'New',
  in_progress: 'Contacting',
  awaiting_manual: 'Awaiting manual',
  ineligible: 'Not applicable',
  confirmed: 'Confirmed',
  cancelled_by_customer: 'Cancelled by customer',
  unreachable: 'Unreachable',
  manual_override: 'Manual override',
  halted: 'Halted',
};

const STATUS_TONE: Record<CommsOrderStatus, BadgeTone | undefined> = {
  new: 'info',
  in_progress: 'attention',
  awaiting_manual: 'warning',
  ineligible: undefined,
  confirmed: 'success',
  cancelled_by_customer: 'critical',
  unreachable: 'critical',
  manual_override: 'new',
  halted: undefined,
};

function formatTs(ts: number | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleString('en-GB', { hour12: false });
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

export function CommsOrdersPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const health = useCommsHealth();
  const orders = useCommsOrders({ status: status || undefined, q: q.trim() || undefined });

  const ordersData: CommsOrdersResponse | undefined = orders.data;
  const statusList: CommsOrderStatus[] = ordersData?.statuses ?? (Object.keys(STATUS_LABEL) as CommsOrderStatus[]);
  const statusOptions = useMemo(
    () => [
      { label: 'All statuses', value: '' },
      ...statusList.map((s) => ({ label: STATUS_LABEL[s] ?? s, value: s })),
    ],
    [statusList],
  );

  const items: CommsOrder[] = ordersData?.items ?? [];

  return (
    <Page
      title="Order confirmations"
      subtitle="COD orders held until the customer confirms — WhatsApp, calls, SMS and email arrive in later phases"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/dashboard') }}
      fullWidth
    >
      <BlockStack gap="400">
        {health.isError && (
          <Banner tone="critical" title="Order comms service is unreachable">
            <p>{errorMessage(health.error)}</p>
          </Banner>
        )}
        {health.data && (
          <Card>
            <InlineStack gap="400" wrap>
              <Text as="span" variant="bodySm">
                Service v{health.data.version}
              </Text>
              <Text as="span" variant="bodySm">
                Jobs pending: {health.data.jobs_pending}
              </Text>
              <Text as="span" variant="bodySm" tone={health.data.jobs_failed ? 'critical' : undefined}>
                Jobs failed: {health.data.jobs_failed}
              </Text>
              {Object.entries(health.data.providers).map(([name, ok]) => (
                <Badge key={name} tone={ok ? 'success' : undefined}>
                  {`${name}: ${ok ? 'configured' : 'missing'}`}
                </Badge>
              ))}
            </InlineStack>
          </Card>
        )}

        <Card>
          <BlockStack gap="300">
            <InlineStack gap="300" blockAlign="end" wrap>
              <Box minWidth="220px">
                <Select label="Status" options={statusOptions} value={status} onChange={setStatus} />
              </Box>
              <Box minWidth="280px">
                <TextField
                  label="Search"
                  value={q}
                  onChange={setQ}
                  autoComplete="off"
                  placeholder="Order #, phone or customer"
                  clearButton
                  onClearButtonClick={() => setQ('')}
                />
              </Box>
              <Button onClick={() => void orders.refetch()} loading={orders.isFetching}>
                Refresh
              </Button>
            </InlineStack>
            {orders.isLoading ? (
              <Box padding="400">
                <Spinner size="small" />
              </Box>
            ) : (
              <IndexTable
                resourceName={{ singular: 'order', plural: 'orders' }}
                itemCount={items.length}
                selectable={false}
                headings={[
                  { title: 'Order' },
                  { title: 'Customer' },
                  { title: 'Phone' },
                  { title: 'Payment' },
                  { title: 'Total' },
                  { title: 'Status' },
                  { title: 'Step' },
                  { title: 'Received' },
                ]}
              >
                {items.map((o, index) => (
                  <IndexTable.Row id={String(o.id)} key={o.id} position={index} onClick={() => setSelectedId(o.id)}>
                    <IndexTable.Cell>
                      <Text as="span" fontWeight="semibold">
                        {o.order_name ?? o.shopify_order_id}
                      </Text>
                      {o.hold_applied ? (
                        <Box paddingInlineStart="100" as="span">
                          <Badge tone="attention">Held</Badge>
                        </Box>
                      ) : null}
                      {o.duplicate_of_order_id ? (
                        <Box paddingInlineStart="100" as="span">
                          <Badge tone="warning">Duplicate?</Badge>
                        </Box>
                      ) : null}
                    </IndexTable.Cell>
                    <IndexTable.Cell>{o.customer_name ?? '—'}</IndexTable.Cell>
                    <IndexTable.Cell>{o.phone_e164 ?? <Badge tone="critical">Invalid</Badge>}</IndexTable.Cell>
                    <IndexTable.Cell>{(o.payment_gateways ?? []).join(', ') || '—'}</IndexTable.Cell>
                    <IndexTable.Cell>
                      {o.total_price ? `${o.total_price} ${o.currency ?? ''}` : '—'}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={STATUS_TONE[o.status]}>{STATUS_LABEL[o.status] ?? o.status}</Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>{o.flow?.state ?? '—'}</IndexTable.Cell>
                    <IndexTable.Cell>{formatTs(o.shopify_created_at ?? o.created_at)}</IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            )}
            <Text as="p" variant="bodySm" tone="subdued">
              {ordersData ? `${ordersData.total} orders` : ''}
            </Text>
          </BlockStack>
        </Card>

        <CommsSettingsCard />
      </BlockStack>

      {selectedId !== null && <OrderDetailModal id={selectedId} onClose={() => setSelectedId(null)} />}
    </Page>
  );
}

function OrderDetailModal({ id, onClose }: { id: number; onClose: () => void }) {
  const detail = useCommsOrder(id);
  const data: CommsOrderDetail | undefined = detail.data;
  const act = useCommsOrderAction();
  const [note, setNote] = useState('');
  const run = data?.flow_runs[0];
  const active = run && !run.terminal;
  const events: CommsEvent[] = data?.events ?? [];
  const jobs: CommsJob[] = data?.jobs ?? [];

  const doAction = (action: CommsAction, hours?: number) => {
    act.mutate({ id, action, hours, note: note.trim() || undefined });
  };

  const order = data?.order;
  return (
    <Modal
      open
      onClose={onClose}
      title={order ? `Order ${order.order_name ?? order.shopify_order_id}` : 'Order'}
      size="large"
      primaryAction={
        active
          ? { content: 'Mark confirmed', onAction: () => doAction('confirm'), loading: act.isPending }
          : undefined
      }
      secondaryActions={
        active
          ? [
              { content: 'Cancel order', destructive: true, onAction: () => doAction('cancel') },
              { content: 'Unreachable', onAction: () => doAction('mark_unreachable') },
              { content: 'Snooze 2h', onAction: () => doAction('snooze', 2) },
            ]
          : [{ content: 'Restart flow', onAction: () => doAction(run ? 'restart' : 'start') }]
      }
    >
      <Modal.Section>
        {detail.isLoading || !order ? (
          <Spinner size="small" />
        ) : (
          <BlockStack gap="400">
            {act.isError && (
              <Banner tone="critical" title="Action failed">
                <p>{errorMessage(act.error)}</p>
              </Banner>
            )}
            <InlineStack gap="400" wrap>
              <Badge tone={STATUS_TONE[order.status]}>{STATUS_LABEL[order.status] ?? order.status}</Badge>
              <Text as="span">{order.customer_name ?? '—'}</Text>
              <Text as="span">{order.phone_e164 ?? order.email ?? '—'}</Text>
              <Text as="span">
                {order.total_price} {order.currency}
              </Text>
              <Text as="span">{(order.payment_gateways ?? []).join(', ')}</Text>
              {run && (
                <Text as="span" tone="subdued">
                  Flow: {run.state}
                  {run.next_action_at ? ` · next ${formatTs(run.next_action_at)}` : ''}
                </Text>
              )}
            </InlineStack>
            {order.shipping_address && (
              <Text as="p" variant="bodySm">
                {[
                  order.shipping_address.address1,
                  order.shipping_address.address2,
                  order.shipping_address.city,
                  order.shipping_address.province,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </Text>
            )}
            {order.line_items && order.line_items.length > 0 && (
              <Text as="p" variant="bodySm">
                {order.line_items.map((li) => `${li.quantity ?? 1}× ${li.title ?? ''}`).join(' · ')}
              </Text>
            )}
            <TextField
              label="Note for the Shopify timeline (optional)"
              value={note}
              onChange={setNote}
              autoComplete="off"
              disabled={!active}
            />
            <Divider />
            <Text as="h3" variant="headingSm">
              Timeline
            </Text>
            <BlockStack gap="100">
              {events.map((e) => (
                <InlineStack key={e.id} gap="200" wrap={false}>
                  <Box minWidth="150px">
                    <Text as="span" variant="bodySm" tone="subdued">
                      {formatTs(e.created_at)}
                    </Text>
                  </Box>
                  <Text as="span" variant="bodySm">
                    {e.kind}
                    {e.channel ? ` (${e.channel})` : ''}
                    {e.actor ? ` by ${e.actor}` : ''}
                    {e.detail && Object.keys(e.detail).length ? ` — ${JSON.stringify(e.detail)}` : ''}
                  </Text>
                </InlineStack>
              ))}
            </BlockStack>
            {jobs.length > 0 && (
              <>
                <Divider />
                <Text as="h3" variant="headingSm">
                  Shopify actions
                </Text>
                <BlockStack gap="100">
                  {jobs.map((j) => (
                    <InlineStack key={j.id} gap="200">
                      <Badge tone={j.status === 'sent' ? 'success' : j.status === 'failed' ? 'critical' : 'info'}>
                        {j.status}
                      </Badge>
                      <Text as="span" variant="bodySm">
                        {j.channel}/{j.purpose} · attempts {j.attempts}/{j.max_attempts}
                        {j.last_error ? ` · ${j.last_error}` : ''}
                      </Text>
                    </InlineStack>
                  ))}
                </BlockStack>
              </>
            )}
          </BlockStack>
        )}
      </Modal.Section>
    </Modal>
  );
}

function CommsSettingsCard() {
  const settings = useCommsSettings();
  const update = useUpdateCommsSettings();
  const s = settings.data?.settings;
  if (!s) return null;

  const toggle = (key: string) => (value: boolean) => update.mutate({ [key]: value });
  const number = (key: string) => (value: string) => {
    const n = Number(value);
    if (Number.isInteger(n) && n >= 0) update.mutate({ [key]: n });
  };

  return (
    <Card>
      <BlockStack gap="300">
        <Text as="h2" variant="headingMd">
          Confirmation settings
        </Text>
        {update.isError && (
          <Banner tone="critical" title="Could not save">
            <p>{errorMessage(update.error)}</p>
          </Banner>
        )}
        <InlineStack gap="400" wrap>
          <Checkbox label="Confirmation flow enabled" checked={s.confirmation_enabled} onChange={toggle('confirmation_enabled')} />
          <Checkbox label="Hold fulfillment until confirmed" checked={s.apply_fulfillment_hold} onChange={toggle('apply_fulfillment_hold')} />
          <Checkbox
            label="Auto-cancel in Shopify when the customer cancels (unpaid orders only)"
            checked={s.auto_cancel_on_customer_cancel}
            onChange={toggle('auto_cancel_on_customer_cancel')}
          />
          <Checkbox label="Email customer on auto-cancel" checked={s.auto_cancel_notify_customer} onChange={toggle('auto_cancel_notify_customer')} />
          <Checkbox label="Skip Shopify test orders" checked={s.skip_test_orders} onChange={toggle('skip_test_orders')} />
        </InlineStack>
        <InlineStack gap="400" wrap>
          <Box minWidth="160px">
            <TextField label="WhatsApp wait (hours)" type="number" value={String(s.wa_wait_hours)} onChange={number('wa_wait_hours')} autoComplete="off" />
          </Box>
          <Box minWidth="160px">
            <TextField label="Call attempts (max 3)" type="number" value={String(s.call_attempts)} onChange={number('call_attempts')} autoComplete="off" />
          </Box>
          <Box minWidth="160px">
            <TextField label="Gap between calls (hours)" type="number" value={String(s.call_gap_hours)} onChange={number('call_gap_hours')} autoComplete="off" />
          </Box>
          <Box minWidth="160px">
            <TextField label="SMS wait (hours)" type="number" value={String(s.sms_wait_hours)} onChange={number('sms_wait_hours')} autoComplete="off" />
          </Box>
          <Box minWidth="160px">
            <TextField label="Email wait (hours)" type="number" value={String(s.email_wait_hours)} onChange={number('email_wait_hours')} autoComplete="off" />
          </Box>
        </InlineStack>
        <Text as="p" variant="bodySm" tone="subdued">
          Channels: {Object.entries(s.channels_enabled).map(([k, v]) => `${k} ${v ? 'on' : 'off'}`).join(' · ')} — each channel is
          switched on after its provider is configured and tested.
        </Text>
      </BlockStack>
    </Card>
  );
}
